/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import {
  Component,
  ElementRef,
  ChangeDetectorRef,
  Renderer2,
  NgZone,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';
import { SelectInputModel } from '@typescript-kit/view';
import { BaseDirective } from '../../directive/base.directive';
import { ChangedEvent, TextService, MessageService } from '@typescript-kit/core';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'select[kitModel]',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectInputComponent extends BaseDirective<SelectInputModel> implements OnDestroy {
  private unlistenInput: () => void;
  public isValueOptionMissing: boolean;
  public options: string[];
  public formattedOptions: string[];

  constructor(
    changeDetector: ChangeDetectorRef,
    elementRef: ElementRef,
    renderer: Renderer2,
    ngZone: NgZone,
    textService: TextService,
    protected messageService: MessageService
  ) {
    super(changeDetector, elementRef, renderer, ngZone, textService);
  }

  get hostClass(): string {
    return 'kit-select-input';
  }

  get selectElement(): HTMLSelectElement {
    return this.elementRef.nativeElement;
  }

  ngOnDestroy(): void {
    this.deregisterInputHandler();
    super.ngOnDestroy();
  }

  protected onModelChanged(model: SelectInputModel) {
    super.onModelChanged(model);
    this.refreshOptions();
    this.refreshFormattedOptions();
    this.selectElement.disabled = this.model.isDisabled;
    this.refreshElementValue();
    this.refreshMissingValueOption();
    this.refreshValidity();
    this.refreshErrorClass();
  }

  protected onModelPropertyChanged(event: ChangedEvent): void {
    super.onModelPropertyChanged(event);
    if (event.changes['options']) {
      this.refreshOptions();
    }
    if (event.changes['formattedOptions']) {
      this.refreshFormattedOptions();
    }
    if (event.changes['isDisabled']) {
      this.selectElement.disabled = this.model.isDisabled;
    }
    if (event.changes['value']) {
      this.refreshElementValue();
    }
    if (event.changes['value'] || event.changes['options']) {
      this.refreshMissingValueOption();
    }
    if (event.changes['isValid']) {
      this.refreshValidity();
      this.refreshErrorClass();
    }
  }

  protected refreshModelValue() {
    this.setModelValue(this.selectElement.value);
  }

  protected refreshElementValue() {
    this.selectElement.value = this.getModelValue();
  }

  private refreshMissingValueOption() {
    const modelValue = this.getModelValue();
    this.isValueOptionMissing = !this.options || this.options.findIndex(item => item === modelValue) < 0;
  }

  private refreshOptions() {
    if (this.model && this.model.options) {
      this.options = this.model.options.map(option => option === undefined || option === null ? '' : option.toString());
    } else {
      this.options = [];
    }
  }

  private refreshFormattedOptions() {
    if (this.model && this.model.formattedOptions) {
      this.formattedOptions = this.model.formattedOptions.map((text) => this.textService.getText(text));
    } else {
      this.formattedOptions = [];
    }
  }

  private refreshValidity() {
    const error = this.model.formatError || this.model.validationError;
    this.selectElement.setCustomValidity(error ? this.messageService.getMessage(error) : '');
  }

  private refreshErrorClass() {
    if (this.model.isValid === false) {
      this.renderer.addClass(this.selectElement, 'app-has-error');
    } else {
      this.renderer.removeClass(this.selectElement, 'app-has-error');
    }
  }

  protected onFocus(event: FocusEvent) {
    super.onFocus(event);
    this.registerInputHandler();
  }

  protected onBlur(event: Event) {
    super.onBlur(event);
    this.deregisterInputHandler();
  }


  private getModelValue(): string {
    if (!this.model) {
      return undefined;
    }
    return this.model.value === undefined || this.model.value === null ? '' : this.model.value.toString();
  }

  private setModelValue(value: string) {
    if (!this.model || value === this.getModelValue()) {
      return;
    }
    if (this.options && (!value || typeof this.model.value !== 'string')) {
      // find original empty value (e.g, undefined, null, or '') or original value type (e.g., true, false, 1, ...)
      const optionIndex = this.options.findIndex(option => option === value);
      if (optionIndex >= 0) {
        this.model.value = this.model.options[optionIndex];
        return;
      }
    }
    // use null as default empty value
    this.model.value = value || null;
  }

  private registerInputHandler() {
    if (this.unlistenInput) {
      return;
    }
    this.unlistenInput = this.renderer.listen(this.selectElement, 'change', () => {
      this.ngZone.run(() => this.refreshModelValue());
    });
  }

  private deregisterInputHandler() {
    if (!this.unlistenInput) {
      return;
    }
    this.unlistenInput();
    this.unlistenInput = null;
  }

}
